import {
  LayoutDashboard,
  Users,
  HardHat,
  Receipt,
  Clock,
  FileText,
  Calculator,
  FolderOpen,
  ScanLine,
  BadgeDollarSign,
  CalendarDays,
  Bell,
  CreditCard,
  Settings,
  DoorOpen,
  Shield,
  BrainCircuit,
  Images,
} from 'lucide-react';

// ============================================================================
// Sidebar navigation — single source of truth
// ----------------------------------------------------------------------------
// Phase 28: nav is grouped into labeled sections (CREW / CLIENTS / JOBS /
// FINANCES / SYSTEM / INTELLIGENCE) with Dashboard ungrouped at the very top.
// CREW leads the list: daily-use items (the clock) outrank the org-chart order.
// `NAV_SECTIONS` is the canonical structure; `NAV_ITEMS` remains exported as
// a flat array derived from it so existing consumers (placeholder pages,
// findNavItem, route generators, etc.) keep working unchanged.
//
// Authorization flags (Phase 21, unchanged through Phase 28):
//   `adminOnly: true`  -> hidden from the sidebar AND blocked at the route
//                         level via <RoleRoute role="admin"> in App.js.
//                         Crew Leads must not see or visit these pages.
//   (no flag)          -> visible to all authenticated users (admin +
//                         crew_lead).
//   `ownerOnly: true`  -> legacy alias for adminOnly; SidebarNav honors both.
//
// Section header rendering: SidebarNav filters each section's items by role
// first, then SKIPS the entire section (header + items) when zero items
// remain visible — so a Crew Lead never sees empty headers.
// ============================================================================

export const NAV_SECTIONS = [
  {
    // Dashboard is ungrouped at the top — `label` is null so SidebarNav
    // renders the items with NO header above them.
    key: 'top',
    label: null,
    i18nKey: null,
    items: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        i18nKey: 'nav.dashboard',
        path: '/dashboard',
        end: true,
        icon: LayoutDashboard,
        description:
          'Operational overview, KPIs, and at-a-glance status across every module.',
        adminOnly: true,
      },
    ],
  },
  {
    // CREW sits directly under Dashboard (moved up 2026-09-03): the clock is
    // a multiple-times-a-day tap — the most-used items belong at the top.
    key: 'crew',
    label: 'CREW',
    i18nKey: 'nav.section_crew',
    items: [
      {
        key: 'crew-clock',
        label: 'Crew Clock',
        i18nKey: 'nav.crew_clock',
        path: '/dashboard/crew-clock',
        icon: Clock,
        description:
          'Field time-tracking with geofenced clock-in/out and labor allocation.',
        // visible to crew_lead AND admin
      },
      {
        key: 'crew-portal',
        label: 'Crew Portal',
        i18nKey: 'nav.crew_portal',
        path: '/dashboard/crew-portal',
        icon: Shield,
        description:
          'Mobile-first portal for crews: assignments, clock, and job files.',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'clients',
    label: 'CLIENTS',
    i18nKey: 'nav.section_clients',
    items: [
      {
        key: 'clients-leads',
        label: 'Clients & Leads',
        i18nKey: 'nav.clients',
        path: '/dashboard/clients-leads',
        icon: Users,
        description:
          'Capture leads, qualify prospects, and manage the full client lifecycle.',
        adminOnly: true,
      },
      {
        key: 'estimates',
        label: 'Estimates',
        i18nKey: 'nav.estimates',
        path: '/dashboard/estimates',
        icon: FileText,
        description:
          'Build polished estimates from line items, templates, and saved bid history.',
        adminOnly: true,
      },
      {
        key: 'client-portal',
        label: 'Client Portal',
        i18nKey: 'nav.client_portal',
        path: '/dashboard/client-portal',
        icon: DoorOpen,
        description:
          'Self-serve portal for clients to view jobs, approvals, and invoices.',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'jobs',
    label: 'JOBS',
    i18nKey: 'nav.section_jobs',
    items: [
      {
        key: 'active-jobs',
        label: 'Active Jobs',
        i18nKey: 'nav.jobs',
        path: '/dashboard/active-jobs',
        icon: HardHat,
        description:
          'Track in-flight jobs, crew assignments, and field progress in real time.',
        adminOnly: true,
      },
      {
        key: 'job-costing',
        label: 'Job Tracker',
        i18nKey: 'nav.job_costing',
        path: '/dashboard/job-costing',
        icon: Calculator,
        description:
          'Compare estimates vs. actuals across labor, materials, and overhead.',
        adminOnly: true,
      },
      {
        key: 'job-files',
        label: 'Job Files',
        i18nKey: 'nav.job_files',
        path: '/dashboard/job-files',
        icon: FolderOpen,
        description:
          'Centralized document and photo storage scoped to each job and client.',
        // visible to crew_lead AND admin (operational, non-financial)
      },
      {
        key: 'schedule',
        label: 'Schedule',
        i18nKey: 'nav.schedule',
        path: '/dashboard/schedule',
        icon: CalendarDays,
        description:
          'Crew dispatch, jobsite scheduling, and conflict-aware calendar views.',
        // visible to crew_lead AND admin; RLS filters rows to their assignments
      },
    ],
  },
  {
    key: 'finances',
    label: 'FINANCES',
    i18nKey: 'nav.section_finances',
    items: [
      {
        key: 'invoices',
        label: 'Invoices',
        i18nKey: 'nav.invoices',
        path: '/dashboard/invoices',
        icon: Receipt,
        description:
          'Generate, send, and reconcile invoices with status, aging, and payment links.',
        adminOnly: true,
      },
      {
        key: 'payments',
        label: 'Payments',
        i18nKey: 'nav.payments',
        path: '/dashboard/payments',
        icon: CreditCard,
        description:
          'Accept payments, reconcile deposits, and track outstanding balances.',
        adminOnly: true,
      },
      {
        key: 'receipt-scanner',
        label: 'Receipt Scanner',
        i18nKey: 'nav.receipt_scanner',
        path: '/dashboard/receipt-scanner',
        icon: ScanLine,
        description:
          'Capture receipts on-site, auto-extract totals, and assign to jobs or vendors.',
        adminOnly: true,
      },
      {
        key: 'rebate-tracker',
        label: 'Rebate Tracker',
        i18nKey: 'nav.rebate_tracker',
        path: '/dashboard/rebate-tracker',
        icon: BadgeDollarSign,
        description:
          'Manage manufacturer rebates with deadlines, statuses, and payouts.',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'system',
    label: 'SYSTEM',
    i18nKey: 'nav.section_system',
    items: [
      {
        key: 'reminders',
        label: 'Reminders',
        i18nKey: 'nav.reminders',
        path: '/dashboard/reminders',
        icon: Bell,
        description:
          'Automated and manual reminders for follow-ups, deadlines, and renewals.',
        adminOnly: true,
      },
      {
        key: 'settings',
        label: 'Settings',
        i18nKey: 'nav.settings',
        path: '/dashboard/settings',
        icon: Settings,
        description:
          'Company profile, branding, integrations, taxes, and team permissions.',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'intelligence',
    label: 'INTELLIGENCE',
    i18nKey: 'nav.section_intelligence',
    items: [
      {
        key: 'pq-intelligence',
        label: 'PQ Intelligence',
        i18nKey: 'nav.pq_intelligence',
        path: '/dashboard/pq-intelligence',
        icon: BrainCircuit,
        iconImage: '/pq-intelligence-logo.png',
        brandWordmark: '/brand/pq-wordmark.webp',
        adminOnly: true,
        ownerOnly: true, // retained for any legacy consumer
        description:
          'Margin posture, win rates, and operational signals derived from silent telemetry.',
      },
      {
        key: 'milli',
        label: 'Milli',
        i18nKey: 'nav.milli',
        path: '/milli',
        icon: BrainCircuit,
        adminOnly: true,
        description:
          'Talk to Milli — the K.O. Command orchestrator. Status, tasks, and anything on your mind.',
      },
      {
        key: 'approvals',
        label: 'Approvals',
        i18nKey: 'nav.approvals',
        path: '/dashboard/approvals',
        icon: Shield,
        adminOnly: true,
        description:
          'Decisions K.O. Command escalated for human sign-off before acting.',
      },
      {
        key: 'brand',
        label: 'Brand Library',
        i18nKey: 'nav.brand',
        path: '/dashboard/brand',
        icon: Images,
        adminOnly: true,
        description:
          'The registered brand files Milli composes with, and the design department queue.',
      },
    ],
  },
];

// Derived flat list — preserved for backward compatibility with existing
// consumers (placeholder pages, findNavItem, route generators, etc.). Order
// matches the section order above, items in the order each section lists.
export const NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

export const findNavItem = (key) => NAV_ITEMS.find((n) => n.key === key);
